'use client';

import React, { useRef, useEffect } from 'react';

import { throttle } from 'lodash-es';

import { CloseIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import ClientOnlyPortal from '@components/Drawer/next/ClientOnlyPortal';

import {
  ModalWrapper,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  Footer,
} from './styled';
import { ModalProps } from './props';

export const Modal = ({
  children,
  closeIcon,
  footer,
  onCancel,
  closable = true,
  hideCloseIcon = false,
  zIndex = 1005,
  maskClosable = true,
  width,
  headerMediaQuery,
  backgroundColor,
  padding,
  title,
  height,
  headerElementCss,
  footerStyles,
  contentStyles,
  warning,
  closeElementCss,
  isPhoneFullHeight = true,
  borderRadius,
  modalBodyHeight,
  showBodyMarginTop = true,
  headerHeight,
  modalIconColor,
  preventBodyScroll = true,
  reservationTour,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        maskClosable &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onCancel?.();
      }
    };
    document.addEventListener('mousedown', throttle(handleOutsideClick, 200));
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [onCancel, maskClosable, modalRef]);

  useEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      body.style.overflow = preventBodyScroll ? 'hidden' : 'initial';
    }

    return () => {
      if (body) {
        body.style.overflow = 'initial';
      }
    };
  }, [preventBodyScroll]);

  // CloseButton JSX bloğunu dışarıya çıkarıyoruz
  const renderCloseButton = () => {
    const handleClick = closable ? () => onCancel?.() : undefined;
    if (closeIcon) {
      return (
        <CloseButton
          warning={warning}
          onClick={handleClick}
          $headerMediaQuery={headerMediaQuery}
        >
          {closeIcon}
        </CloseButton>
      );
    }

    return (
      <CloseButton
        warning={warning}
        onClick={handleClick}
        $headerMediaQuery={headerMediaQuery}
        $closeElementCss={closeElementCss}
      >
        <CustomIcon
          fontSize={24}
          paletteColor={modalIconColor ?? 'brownishGrey'}
          icon={CloseIcon}
        />
      </CloseButton>
    );
  };

  return (
    <ClientOnlyPortal selector='body'>
      <ModalWrapper zIndex={zIndex}>
        <ModalContent
          borderRadius={borderRadius}
          $headerMediaQuery={headerMediaQuery}
          ref={modalRef}
          width={width}
          backgroundColor={backgroundColor}
          padding={padding}
          height={height}
          style={contentStyles}
          isPhoneFullHeight={isPhoneFullHeight}
        >
          {title || !hideCloseIcon ? (
            <ModalHeader
              $headerElementCss={headerElementCss}
              $headerMediaQuery={headerMediaQuery}
              headerHeight={headerHeight}
            >
              <ModalTitle>{title}</ModalTitle>
              {!hideCloseIcon && renderCloseButton()}
            </ModalHeader>
          ) : null}
          <ModalBody
            reservationTour={reservationTour}
            modalBodyHeight={modalBodyHeight}
            $headerMediaQuery={headerMediaQuery}
            showMarginTop={showBodyMarginTop}
          >
            {children}
          </ModalBody>

          {footer && (
            <Footer style={footerStyles} padding={padding}>
              {footer}
            </Footer>
          )}
        </ModalContent>
      </ModalWrapper>
    </ClientOnlyPortal>
  );
};

export default Modal;
